import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { businessesApi } from "../lib/businessesApi";
import type { UpdateBusinessPayload, AddMemberPayload, UpdateMemberPayload } from "../lib/businessesApi";

export const BUSINESS_KEYS = {
  all: ["businesses"] as const,
  list: () => [...BUSINESS_KEYS.all, "list"] as const,
  members: (bizId: string) => [...BUSINESS_KEYS.all, "members", bizId] as const,
};

export function useBusinesses(enabled = true) {
  return useQuery({
    queryKey: BUSINESS_KEYS.list(),
    queryFn: () => businessesApi.list(),
    enabled,
  });
}

export function useCreateBusiness() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: businessesApi.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: BUSINESS_KEYS.all }),
  });
}

export function useUpdateBusiness(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateBusinessPayload) => businessesApi.update(businessId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: BUSINESS_KEYS.all }),
  });
}

export function useBusinessMembers(businessId: string) {
  return useQuery({
    queryKey: BUSINESS_KEYS.members(businessId),
    queryFn: () => businessesApi.listMembers(businessId),
    enabled: !!businessId,
  });
}

export function useAddBusinessMember(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AddMemberPayload) => businessesApi.addMember(businessId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: BUSINESS_KEYS.members(businessId) }),
  });
}

export function useUpdateBusinessMember(businessId: string, userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateMemberPayload) =>
      businessesApi.updateMember(businessId, userId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: BUSINESS_KEYS.members(businessId) }),
  });
}

export function useMemberMembershipCount(businessId: string, userId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...BUSINESS_KEYS.all, "membership-count", businessId, userId] as const,
    queryFn: () => businessesApi.memberMembershipCount(businessId, userId),
    enabled: enabled && !!businessId && !!userId,
  });
}

export function useRemoveBusinessMember(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => businessesApi.removeMember(businessId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: BUSINESS_KEYS.members(businessId) }),
  });
}

export function useSetMemberStatus(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, status }: { userId: string; status: "ACTIVE" | "INACTIVE" }) =>
      businessesApi.updateMember(businessId, userId, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: BUSINESS_KEYS.members(businessId) }),
  });
}

export function useUploadBusinessLogo(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ imageData, contentType }: { imageData: string; contentType: string }) =>
      businessesApi.uploadLogo(businessId, imageData, contentType),
    onSuccess: () => qc.invalidateQueries({ queryKey: BUSINESS_KEYS.all }),
  });
}
